import React from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { Gauge } from "@mui/x-charts/Gauge";
import { LineChart } from "@mui/x-charts/LineChart";

const DEFAULT_COLOR = "#005AA6";

const getNumericValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatCompactNumber = (value, decimals = 1) => new Intl.NumberFormat(undefined, {
  notation: Math.abs(value) >= 1000 ? "compact" : "standard",
  maximumFractionDigits: decimals,
}).format(value);

const formatBytes = (value, decimals = 1) => {
  if (value === 0) return "0 B";
  const units = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(Math.abs(value)) / Math.log(1024)),
    units.length - 1
  );
  return `${(value / (1024 ** unitIndex)).toFixed(decimals)} ${units[unitIndex]}`;
};

const formatPanelValue = (value, panel) => {
  if (panel.formattedValue != null && value === panel.value) return panel.formattedValue;
  const numericValue = getNumericValue(value);
  if (numericValue == null) return value == null ? "No data" : String(value);

  const decimals = Number.isInteger(panel.decimals) ? panel.decimals : 1;
  switch ((panel.unit || "").toLowerCase()) {
    case "percent":
    case "percent (0-100)":
      return `${numericValue.toFixed(decimals)}%`;
    case "percentunit":
    case "percent (0.0-1.0)":
      return `${(numericValue * 100).toFixed(decimals)}%`;
    case "bytes":
    case "decbytes":
      return formatBytes(numericValue, decimals);
    case "milliseconds":
    case "ms":
      return `${formatCompactNumber(numericValue, decimals)} ms`;
    case "seconds":
    case "s":
      return `${formatCompactNumber(numericValue, decimals)} s`;
    default:
      return formatCompactNumber(numericValue, decimals);
  }
};

const getPoint = (point) => {
  if (Array.isArray(point)) return { x: point[0], y: getNumericValue(point[1]) };
  return {
    x: point?.time ?? point?.timestamp ?? point?.x ?? point?.label,
    y: getNumericValue(point?.value ?? point?.y),
  };
};

const buildTimeSeriesDataset = (series = []) => {
  const rows = new Map();
  series.forEach((item, seriesIndex) => {
    (item.points || []).forEach((rawPoint) => {
      const point = getPoint(rawPoint);
      const timestamp = new Date(point.x).getTime();
      if (!Number.isFinite(timestamp)) return;
      const row = rows.get(timestamp) || { time: new Date(timestamp) };
      row[`series_${seriesIndex}`] = point.y;
      rows.set(timestamp, row);
    });
  });
  return [...rows.entries()].sort(([left], [right]) => left - right).map(([, row]) => row);
};

const buildCategoryDataset = (series = []) => {
  const rows = new Map();
  series.forEach((item, seriesIndex) => {
    (item.points || []).forEach((rawPoint) => {
      const point = getPoint(rawPoint);
      const rawCategory = point.x;
      const timestamp = typeof rawCategory === "number" ? new Date(rawCategory) : null;
      const category = timestamp && Number.isFinite(timestamp.getTime())
        ? timestamp.toLocaleString([], { month: "short", day: "numeric", hour: "numeric" })
        : String(rawCategory ?? "");
      const row = rows.get(category) || { category };
      row[`series_${seriesIndex}`] = point.y;
      rows.set(category, row);
    });
  });
  return [...rows.values()];
};

const EmptyPanel = ({ height }) => (
  <Box sx={{ height, display: "grid", placeItems: "center", color: "text.secondary" }}>
    <Typography variant="body2">No data</Typography>
  </Box>
);

const StatPanel = ({ panel, height }) => {
  const color = panel.color || DEFAULT_COLOR;
  const value = getNumericValue(panel.value);
  const min = getNumericValue(panel.min) ?? 0;
  const max = getNumericValue(panel.max) ?? 100;
  const progress = value == null || max === min
    ? 0
    : Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  return (
    <Box sx={{ minHeight: height, display: "flex", flexDirection: "column", justifyContent: "center", px: 2 }}>
      <Typography
        component="div"
        sx={{ color, fontSize: { xs: "2rem", md: "2.75rem" }, fontWeight: 700, overflowWrap: "anywhere" }}
      >
        {formatPanelValue(panel.value, panel)}
      </Typography>
      {panel.subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {panel.subtitle}
        </Typography>
      )}
      {panel.showProgress && value != null && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 2, height: 6, borderRadius: 1, "& .MuiLinearProgress-bar": { backgroundColor: color } }}
        />
      )}
    </Box>
  );
};

const GaugePanel = ({ panel, height }) => {
  const value = getNumericValue(panel.value);
  if (value == null) return <EmptyPanel height={height} />;

  return (
    <Gauge
      height={height}
      value={value}
      valueMin={getNumericValue(panel.min) ?? 0}
      valueMax={getNumericValue(panel.max) ?? 100}
      startAngle={-110}
      endAngle={110}
      text={formatPanelValue(panel.value, panel)}
      sx={{
        "& .MuiGauge-valueArc": { fill: panel.color || DEFAULT_COLOR },
        "& .MuiGauge-valueText": { fontSize: 28, fontWeight: 700 },
      }}
    />
  );
};

const TimeSeriesPanel = ({ panel, height }) => {
  const dataset = buildTimeSeriesDataset(panel.series);
  if (!dataset.length) return <EmptyPanel height={height} />;

  return (
    <LineChart
      dataset={dataset}
      height={height}
      series={(panel.series || []).map((series, index) => ({
        dataKey: `series_${index}`,
        label: series.name || `Series ${index + 1}`,
        color: series.color,
        connectNulls: true,
        showMark: dataset.length <= 30,
        valueFormatter: (value) => formatPanelValue(value, panel),
      }))}
      xAxis={[{
        dataKey: "time",
        scaleType: "time",
        valueFormatter: (value) => new Date(value).toLocaleString([], {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }),
      }]}
      yAxis={[{ valueFormatter: (value) => formatPanelValue(value, panel) }]}
      grid={{ horizontal: true }}
      margin={{ left: 70, right: 20, top: 30, bottom: 35 }}
    />
  );
};

const BarPanel = ({ panel, height }) => {
  const dataset = buildCategoryDataset(panel.series);
  if (!dataset.length) return <EmptyPanel height={height} />;

  return (
    <BarChart
      dataset={dataset}
      height={height}
      series={(panel.series || []).map((series, index) => ({
        dataKey: `series_${index}`,
        label: series.name || `Series ${index + 1}`,
        color: series.color,
        stack: series.stack,
        valueFormatter: (value) => formatPanelValue(value, panel),
      }))}
      xAxis={[{ dataKey: "category", scaleType: "band" }]}
      yAxis={[{ valueFormatter: (value) => formatPanelValue(value, panel) }]}
      grid={{ horizontal: true }}
      margin={{ left: 70, right: 20, top: 30, bottom: 45 }}
    />
  );
};

const DataTablePanel = ({ panel, height }) => {
  const rows = panel.rows || [];
  const columns = panel.columns || Object.keys(rows[0] || {}).map((key) => ({ key, label: key }));
  if (!rows.length) return <EmptyPanel height={height} />;

  return (
    <TableContainer sx={{ maxHeight: height }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key || column.field}>{column.label || column.key || column.field}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={row.id ?? rowIndex} hover>
              {columns.map((column) => {
                const key = column.key || column.field;
                return (
                  <TableCell key={key}>
                    {formatPanelValue(row[key], { ...panel, unit: column.unit, formattedValue: null })}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const PanelVisualization = ({ panel, height }) => {
  switch ((panel.type || "").toLowerCase()) {
    case "stat":
      return <StatPanel panel={panel} height={height} />;
    case "gauge":
      return <GaugePanel panel={panel} height={height} />;
    case "timeseries":
    case "time-series":
    case "line":
      return <TimeSeriesPanel panel={panel} height={height} />;
    case "bar":
    case "barchart":
      return <BarPanel panel={panel} height={height} />;
    case "table":
      return <DataTablePanel panel={panel} height={height} />;
    case "text":
      return (
        <Typography sx={{ minHeight: height, p: 2, whiteSpace: "pre-wrap" }}>
          {panel.text || ""}
        </Typography>
      );
    default:
      return <EmptyPanel height={height} />;
  }
};

const getPanelHeight = (panel) => {
  if (["stat", "gauge"].includes((panel.type || "").toLowerCase())) return 210;
  const gridHeight = panel.gridPos?.h || panel.layout?.h;
  return Math.min(480, Math.max(280, gridHeight ? gridHeight * 24 : 320));
};

export default function KpiDashboardPanel({ panel, comparisonPanel, primaryLabel, comparisonLabel }) {
  const height = Math.max(getPanelHeight(panel), comparisonPanel ? getPanelHeight(comparisonPanel) : 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        borderColor: "#D8E0EA",
        minWidth: 0,
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #E7ECF2" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#172B4D" }}>
          {panel.title}
        </Typography>
        {panel.description && (
          <Typography variant="caption" color="text.secondary">
            {panel.description}
          </Typography>
        )}
      </Box>
      {comparisonPanel ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
          <Box sx={{ minWidth: 0, borderRight: { lg: "1px solid #E7ECF2" }, borderBottom: { xs: "1px solid #E7ECF2", lg: 0 } }}>
            <Typography variant="caption" sx={{ display: "block", px: 2, pt: 1.5, color: "#52657A", fontWeight: 700 }}>
              {primaryLabel}
            </Typography>
            <PanelVisualization panel={panel} height={height} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: "block", px: 2, pt: 1.5, color: "#52657A", fontWeight: 700 }}>
              {comparisonLabel}
            </Typography>
            <PanelVisualization panel={comparisonPanel} height={height} />
          </Box>
        </Box>
      ) : (
        <PanelVisualization panel={panel} height={height} />
      )}
    </Paper>
  );
}